import './ProductBox.css';
import { memo, useEffect, useState } from 'react';
import { ApiProduct, ApiSpecialsProduct } from '../../../types';
import { FavoriteButton } from '../../Favorites/FavoriteButton';
import { useCart } from '../../../state/cart';
import { Link } from 'react-router-dom';
import { useFormatting, useTranslation } from '../../../i18n/i18n';
import { Img } from '../../Img/Img';
import { QuantityInput } from '../../Form/QuantityInput';
import { Button } from '../../Form/Button';
import { useConfig } from '../../../config';
import { FlyToCartContainer } from '../../FlyToCart/FlyToCart';
import { DeliveryTime } from '../../product/DeliveryTime';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { PriceTagSimple, getSpecialPrice } from '../../../components/PriceTagSimple/PriceTagSimple';
import { EcoLabels } from '../../../components/EcoLabel/EcoLabels';
import { useCriteo } from '../../../hooks/useCriteo';
import { ProductContainer } from '../../../state/product';
import { useLoop54Events } from '../../../hooks/useLoop54Events';
import { useSession } from '../../../state/session';
import { Icon, lock, pencil_edit } from '../../Icon/Icon';
import { UiContainer } from '../../../state/ui';
import { MetaContainer } from '../../../state/meta';
import { calculateVatMultiplier } from '../../../utils/vatUtils';

type ProductBoxProps = {
	product: ApiProduct;
	highlight?: boolean;
	className?: string;
	onPurchaseCallback?: () => void;
	renderedInSearchResults?: boolean;
	disabled?: boolean;
	displayLockedUntil?: boolean;
	openInNewTab?: boolean;
	onChangeExpiredProduct?: (productId: number) => void;
};

/**
 * Asks for a render when a special price falls due. Before the box was memoised this rode along
 * with whatever else re-rendered the listing, which meant an expired price could sit on screen
 * until something unrelated happened; a box that keeps still has to keep its own time.
 */
function useDeadlineRerender(specialsProduct: ApiSpecialsProduct | undefined) {
	const [, setPassed] = useState(0);
	const dueAt = nextDeadlineAt(specialsProduct);

	useEffect(() => {
		if (dueAt === null) return;

		let timer: ReturnType<typeof setTimeout>;
		// setTimeout counts in a signed 32-bit int and fires at once when given more, so a deadline
		// further off than that is waited for in stretches rather than missed
		const wait = () => {
			const remaining = dueAt - Date.now();
			if (remaining <= 0) {
				setPassed((passed) => passed + 1);
				return;
			}
			timer = setTimeout(wait, Math.min(remaining, 2 ** 31 - 1));
		};

		wait();
		return () => clearTimeout(timer);
	}, [dueAt]);
}

/*
 * Memoised because a listing re-renders for reasons that have nothing to do with the boxes already
 * on screen - "Visa fler" appending a page is the plain case, and the field telemetry has it as 8%
 * of the interactions over 200ms, 92% of that spent in our own rendering. The props are the product
 * and a few flags, so the default comparison is the right one.
 */
export const ProductBox = memo(function ProductBox({
	product,
	highlight,
	className,
	onPurchaseCallback,
	renderedInSearchResults: cameFromSearchResults,
	disabled,
	displayLockedUntil,
	openInNewTab,
	onChangeExpiredProduct,
}: ProductBoxProps): JSX.Element {
	const { id, name, price, campaignPrice, image, bundleSize, perma, ecoLabels, deliveryClass, deliveryNotice } =
		product;
	const { setQuantity: setCartQuantity, currentCount } = useCart();
	const { currencyCode } = useConfig();
	const [quantityField, setQuantityField] = useState(bundleSize);
	const t = useTranslation().product;
	const { flyToCart } = FlyToCartContainer.useContainer();
	const { sendEvent, sendTorkEvent } = useAnalytics();
	const { sendCriteoEvent } = useCriteo();
	const hasSpecialPrice = !!getSpecialPrice(id);
	const { getFinalPrice, specialPricesIndex } = ProductContainer.useContainer();
	const { store } = useConfig();
	const { sendLoop54Event } = useLoop54Events();
	const { isLoggedIn } = useSession();
	const { marketing } = MetaContainer.useContainer().trackingConsents;
	useDeadlineRerender(specialPricesIndex ? specialPricesIndex[id] : undefined);

	function buy() {
		if (onPurchaseCallback) onPurchaseCallback();
		setCartQuantity(id, currentCount(id) + quantityField);
		flyToCart();
		sendEvent({
			event: 'add_to_cart',
			ecommerce: {
				value: price,
				currency: currencyCode,
				items: [
					{
						item_id: String(id),
						item_name: name,
						price: price,
						quantity: quantityField,
					},
				],
			},
		});

		if (store === 'kgno' && product.name.toLowerCase().includes('tork')) {
			sendTorkEvent('AddToCart', {
				revenue: price * quantityField,
			});
		}

		sendLoop54Event({ events: [{ type: 'addtocart', entity: { type: 'Product', id: product.id + '' } }] });

		sendCriteoEvent({
			event: 'addToCart',
			item: [
				{
					id: id + '',
					price: price,
					quantity: quantityField,
				},
			],
		});

		if (typeof window.fbq !== 'undefined') {
			if (store === 'kgno') {
				if (product.name.toLowerCase().includes('tork') && marketing) {
					window.fbq('track', 'AddToCart', {
						value: price * quantityField,
						currency: 'NOK',
					});
				}
			} else {
				window.fbq('track', 'AddToCart', {
					content_type: 'product',
					content_ids: [id + ''],
					content_name: name,
					value: quantityField * price,
					currency: currencyCode,
				});
			}
		}
	}

	const size = { width: 292, height: 292 };
	const specialPriceProduct = specialPricesIndex ? specialPricesIndex[product.id] : undefined;
	const isLocked = specialPriceIsLocked(specialPriceProduct);

	return (
		<product-box
			class={`${className ? className : ''} ${highlight ? 'highlight' : product.campaignPrice ? 'campaign' : ''}`}
		>
			{productBadge(hasSpecialPrice, campaignPrice > 0, !!highlight, specialPriceProduct)}
			<FavoriteButton id={product.id} />
			{ecoLabels && ecoLabels.length > 0 ? (
				<EcoLabels ecoLabels={ecoLabels} isLoggedIn={isLoggedIn} size={22} />
			) : (
				<div />
			)}
			{displayLockedUntil &&
				specialPriceProduct &&
				(!isLocked ? (
					<ChangeProductBadge onClick={() => onChangeExpiredProduct && onChangeExpiredProduct(product.id)} />
				) : (
					<LockedUntilDateBadge lockedUntilDate={specialPriceProduct.lockedUntil} hasExpired={false} />
				))}
			<Link
				to={{ pathname: '/' + perma, state: { cameFromSearchResults } }}
				className={`product-link ${specialPriceHasExpired(specialPriceProduct) ? 'transparent-1' : ''}`}
				target={openInNewTab ? '_blank' : undefined}
			>
				<Img alt={name} src={image} transparent={highlight} {...size} imgWidth={175} imgHeight={175} />
				<div className="product-name">{name}</div>
				<div className="price-container">
					<PriceTagSimple
						price={getFinalPrice(id, price, 1, campaignPrice)}
						isCampaign={campaignPrice > 0 || !!specialPriceProduct}
						vatRate={product.vatRate}
						unit={product.unit}
					></PriceTagSimple>
					<DeliveryTime
						deliveryNotice={deliveryNotice}
						deliveryClass={deliveryClass}
						textLength={'short-one-line'}
					/>
				</div>
			</Link>
			<div className={`buy-area ${specialPriceHasExpired(specialPriceProduct) ? 'transparent-1' : ''}`}>
				<div className="quantity-input">
					<QuantityInput
						iconSize={19}
						defaultValue={quantityField}
						step={bundleSize}
						onBlur={setQuantityField}
						disabled={disabled}
					/>
				</div>
				<Button
					disabled={disabled || specialPriceHasExpired(specialPriceProduct)}
					className="buy plausible-event-name=Add+to+Cart"
					onClick={() => buy()}
				>
					{t.buyNow}
				</Button>
			</div>
		</product-box>
	);
});

const LockedUntilDateBadge = ({
	lockedUntilDate,
	hasExpired,
}: {
	lockedUntilDate: string;
	hasExpired: boolean;
}): JSX.Element | null => {
	if (hasExpired) return null;
	return (
		<div className="locked-until-date-badge">
			<Icon shape={lock} size={12} />
			<div>{lockedUntilDate.split(' ')[0]}</div>
		</div>
	);
};

const ChangeProductBadge = ({ onClick }: { onClick: () => void }) => {
	const t = useTranslation().specialPrices;
	return (
		<div className="expired-badge" onClick={onClick} style={{ cursor: 'pointer' }}>
			<Icon shape={pencil_edit} size={16} />
			<div>{t.changeProduct}</div>
		</div>
	);
};

/**
 * When the box next has something to change by itself: a special price expires, or its lock lifts,
 * while the page sits open. Absolute, so a render in between does not move it.
 */
export function nextDeadlineAt(specialsProduct: ApiSpecialsProduct | undefined, now = Date.now()): number | null {
	const deadlines = [specialsProduct?.expires, specialsProduct?.lockedUntil]
		.map((value) => (value ? new Date(value).getTime() : NaN))
		// the api writes a missing date as one far in the past rather than leaving it out
		.filter((time) => !Number.isNaN(time) && time > new Date('2000-01-01T00:00:00').getTime() && time > now);

	return deadlines.length ? Math.min(...deadlines) : null;
}

export const specialPriceHasExpired = (specialsProduct: ApiSpecialsProduct | undefined) => {
	if (!specialsProduct?.expires) return false;
	const expiryDate = new Date(specialsProduct.expires);
	if (Number.isNaN(expiryDate.getTime()) || expiryDate < new Date('2000-01-01T00:00:00')) return false;

	// due on the millisecond, so the timer in useDeadlineRerender and this agree on the boundary:
	// it schedules only strictly future deadlines, and a deadline that has just arrived is expired
	return expiryDate <= new Date();
};

const specialPriceIsLocked = (specialsProduct: ApiSpecialsProduct | undefined) => {
	if (!specialsProduct) return false;
	const lockedUntilDate = new Date(specialsProduct.lockedUntil);
	return lockedUntilDate > new Date();
};

export function productBadge(
	hasSpecialPrice: boolean,
	isCampaign: boolean,
	highlighted: boolean,
	selectedSpecialPriceProduct: ApiSpecialsProduct | undefined,
	className?: string
): JSX.Element | null {
	const s = useTranslation().sort;
	const t = { ...useTranslation().campaigns, ...useTranslation().specialPrices };
	const ui = UiContainer.useContainer();
	const { asPrice } = useFormatting();
	if (selectedSpecialPriceProduct) {
		if (specialPriceHasExpired(selectedSpecialPriceProduct)) {
			return <div className="ribbon price-expired">{t.productExpired}</div>;
		}
		const { vatRate } = selectedSpecialPriceProduct.product;
		const vatScale = calculateVatMultiplier(vatRate, ui.priceWithVat);
		const priceDiff = asPrice(
			selectedSpecialPriceProduct.product.price * vatScale - selectedSpecialPriceProduct.newPrice * vatScale
		);
		return (
			<div className="special-price-badges">
				<div className="ribbon special-price">{t.specialPrice}</div>
				<div className="ribbon special-price">-{priceDiff}</div>
			</div>
		);
	}
	if (hasSpecialPrice) return <div className="ribbon special-price">{t.contractPrice}</div>;
	if (isCampaign) return <div className={'ribbon ' + className}>{t.campaign}</div>;
	if (highlighted) return <div className={'ribbon ' + className}>{s.popular}</div>;
	return null;
}
